import { z } from 'zod';

//#region Payment Profile
const basePaymentProfileSchema = z.object({
  feeType: z.enum(['fixed_amount', 'percentage', 'none']).default('none'),
  platformProcessingFee: z.number().nonnegative().default(0),
  sensitive: z.literal(true).default(true),
});

export const stripePaymentProfileSchema = basePaymentProfileSchema.extend({
  paymentProcessor: z.literal('stripe'),
  paymentAccount: z.string(),
  remittanceSchedule: z.enum(['daysPrior', 'immediate']), // The schedule of payment "daysPrior", "dayOf", "immediate"
  remittancePeriod: z.number().nonnegative(), // number of days before a scheduled event payment should be processed,
});

export type StripePaymentProfile = z.infer<typeof stripePaymentProfileSchema>;

const issuingPaymentProfileSchema = basePaymentProfileSchema.extend({
  paymentProcessor: z.literal('issuing'),
});

const invoicePaymentProfileSchema = basePaymentProfileSchema.extend({
  paymentProcessor: z.literal('invoice'),
});

export const paymentProfileCapabilitySchema = z.discriminatedUnion(
  'paymentProcessor',
  [
    stripePaymentProfileSchema,
    issuingPaymentProfileSchema,
    invoicePaymentProfileSchema,
  ]
);
export type PaymentProfileCapability = z.infer<
  typeof paymentProfileCapabilitySchema
>;
//#endregion

//#region Booking Method
const bookingMethodKindEnum = z.enum([
  'email',
  'fareharbor',
  'peek',
  'redeam',
  'singenuity',
  'web',
]);

export const bookingMethodCapabilitySchema = z.discriminatedUnion('kind', [
  z.object({ kind: bookingMethodKindEnum }),
]);
export type BookingMethodCapability = z.infer<
  typeof bookingMethodCapabilitySchema
>;
//#endregion

//#region Commission
const commissionCapabilitySchema = z.object({
  commisionRate: z.number().nonnegative(),
  commisionType: z.enum(['percentage', 'fixed_amount']),
  sensitive: z.literal(true).default(true),
});
//#endregion

//#region Booking Confirmation
export const notificationCapabilitiesChannelEnum = z.enum([
  'integration',
  'iterable',
  'manual',
]);
export const integrationNotificationCapabilityRecipientEnum = z.enum([
  'batch',
  'user',
]);

const manualBookingNotificationSchema = z.object({
  channel: z
    .literal(notificationCapabilitiesChannelEnum.Values.manual)
    .describe('Notifications sent manually by Batch'),
});
const iterableBookingNotificationSchema = z.object({
  channel: z
    .literal(notificationCapabilitiesChannelEnum.Values.iterable)
    .describe('Notifications sent via Iterable'),
});
const integrationBookingNotificationSchema = z.object({
  channel: z
    .literal(notificationCapabilitiesChannelEnum.Values.integration)
    .describe('Notifications sent via booking integration'),
  recipient: integrationNotificationCapabilityRecipientEnum.describe(
    'Who will receive a notification'
  ),
});

const bookingConfirmationCapabilitySchema = z
  .discriminatedUnion('channel', [
    integrationBookingNotificationSchema,
    iterableBookingNotificationSchema,
    manualBookingNotificationSchema,
  ])
  .describe('Settings for booking confirmation notification capability');

export type BookingConfirmationCapability = z.infer<
  typeof bookingConfirmationCapabilitySchema
>;
//#endregion

const capabilitiesSchema = z
  .object({
    bookingConfirmation: bookingConfirmationCapabilitySchema,
    bookingMethod: bookingMethodCapabilitySchema,
    commission: commissionCapabilitySchema,
    paymentProfile: paymentProfileCapabilitySchema,
  })
  .partial();
export type Capabilities = z.infer<typeof capabilitiesSchema>;
